import React, { useMemo, useRef, useState } from 'react';
import { Boxes, ChevronLeft, ChevronRight, Folder, HardDrive, Loader, Search, Send, X } from 'lucide-react';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useCollectionsStore } from '../store/collectionsStore';
import { collectionService } from '../services/collectionService';
import { loadWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import {
  shareToChannel,
  buildSnapshotPayload,
  assertSnapshotSize,
  hydrateWorkspaceSnapshotItem,
} from '../utils/chatShare';
import { encryptSnapshotPayload } from '../utils/shareEnvelope';
import ShareCredentialsToggle from './ShareCredentialsToggle';

function methodColor(method) {
  switch ((method || 'GET').toUpperCase()) {
    case 'GET':     return '#22c55e';
    case 'POST':    return '#f59e0b';
    case 'PUT':     return '#f97316';
    case 'DELETE':  return '#ef4444';
    case 'PATCH':   return '#a855f7';
    case 'HEAD':    return '#60a5fa';
    default:        return '#6b7280';
  }
}

// Local/git workspaces have no server rows — they share as frozen snapshots.
function isSnapshotWs(w) {
  return w?.storage === 'local' || w?.storage === 'git';
}

function matches(query, ...fields) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return fields.some((f) => (f || '').toLowerCase().includes(q));
}

/**
 * The composer's share picker: a Workspace → Collections → Requests drilldown
 * with search at every level. Any level can be shared whole — the workspace
 * row, a collection row, or a single request. On pick it mints a share link
 * scoped to `channelId` and hands the finished message metadata to `onShare`.
 */
export default function ShareDrilldownPicker({ channelId, onShare, onClose }) {
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const liveCollections = useCollectionsStore((s) => s.collections);

  const [level, setLevel] = useState('workspaces');
  const [pickedWs, setPickedWs] = useState(null);
  const [pickedCol, setPickedCol] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  /**
   * Same switch, same copy, same default as the sidebar's "Share → In Chat"
   * modal — one component so the two surfaces cannot drift apart. Leaving this
   * surface silently redacting with no control was the alternative, and it
   * strands the user who deliberately wants to send a working collection.
   */
  const [includeCredentials, setIncludeCredentials] = useState(false);
  // workspaceId -> collections, for the popover's lifetime.
  const cacheRef = useRef(new Map());
  // workspaceId -> full stored snapshot (snapshot workspaces only), so
  // shareWorkspace can reuse it without a second store read.
  const snapCacheRef = useRef(new Map());
  const [fetchedCols, setFetchedCols] = useState(null);

  const isActiveWs = (w) => currentWorkspace && String(w.id) === String(currentWorkspace.id);

  // Routed via workspaceStoreRouter: git workspaces live in SQLite/YAML, not
  // the legacy local-JSON store.
  const loadSnapshotFor = async (w) => {
    const key = String(w.id);
    if (snapCacheRef.current.has(key)) return snapCacheRef.current.get(key);
    const snap = await loadWorkspaceSnapshot(w);
    snapCacheRef.current.set(key, snap);
    return snap;
  };

  const loadCollectionsFor = async (w) => {
    const key = String(w.id);
    if (isActiveWs(w)) return liveCollections || [];
    if (cacheRef.current.has(key)) return cacheRef.current.get(key);
    let cols;
    if (isSnapshotWs(w)) {
      const snap = await loadSnapshotFor(w);
      cols = snap?.collections || [];
    } else {
      const data = await collectionService.getCollections(w.id);
      cols = Array.isArray(data) ? data : data?.collections || [];
    }
    cacheRef.current.set(key, cols);
    return cols;
  };

  const drillIntoWorkspace = async (w) => {
    setPickedWs(w);
    setPickedCol(null);
    setLevel('collections');
    setQuery('');
    setError('');
    // A decision made about one workspace must not carry into the next one
    // picked inside the same popover (review N12). `ShareToChatModal` resets on
    // `[isOpen, target]`; this is the picker's equivalent.
    setIncludeCredentials(false);
    if (isActiveWs(w)) return;
    setLoading(true);
    try {
      setFetchedCols(await loadCollectionsFor(w));
    } catch (e) {
      setError(e.message || 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  };

  const drillIntoCollection = (c) => {
    setPickedCol(c);
    setLevel('requests');
    setQuery('');
    setError('');
  };

  const goBack = () => {
    setQuery('');
    setError('');
    if (level === 'requests') {
      setPickedCol(null);
      setLevel('collections');
    } else if (level === 'collections') {
      setPickedWs(null);
      setFetchedCols(null);
      setLevel('workspaces');
    }
  };

  const doShare = async ({ type, item, workspace, snapshotItem = null }) => {
    if (sending) return;
    setSending(true);
    setError('');
    try {
      const snapshot = isSnapshotWs(workspace);
      // Encrypted before the size check — the 2 MB cap applies to the bytes
      // that actually travel, and base64 ciphertext is ~4/3 the plaintext.
      const payload = snapshot
        ? assertSnapshotSize(
            await encryptSnapshotPayload(
              channelId,
              type,
              buildSnapshotPayload(type, snapshotItem || item, { includeCredentials })
            )
          )
        : null;
      const metadata = await shareToChannel(channelId, {
        type,
        item,
        workspaceName: type === 'workspace' ? null : workspace?.name,
        payload,
      });
      onShare(metadata);
      onClose();
    } catch (e) {
      setError(e.message || 'Failed to share');
      setSending(false);
    }
  };

  const shareWorkspace = async (w) => {
    if (sending) return;
    if (!isSnapshotWs(w)) {
      doShare({ type: 'workspace', item: w, workspace: w });
      return;
    }
    // Snapshot path needs the full collection tree AND the variable fields
    // (the fork's environments/globals come from them).
    setSending(true);
    setError('');
    try {
      const cols = await loadCollectionsFor(w);
      const snap = await loadSnapshotFor(w); // cache hit after loadCollectionsFor
      const snapshotItem = await hydrateWorkspaceSnapshotItem(w, cols, snap);
      setSending(false);
      doShare({ type: 'workspace', item: w, workspace: w, snapshotItem });
    } catch (e) {
      setError(e.message || 'Failed to load workspace data');
      setSending(false);
    }
  };

  const collections = pickedWs && isActiveWs(pickedWs) ? liveCollections || [] : fetchedCols || [];

  const visibleWorkspaces = useMemo(
    () => (workspaces || []).filter((w) => w.id && matches(query, w.name)),
    [workspaces, query]
  );
  const visibleCollections = collections.filter((c) => matches(query, c.name));
  const visibleRequests = (pickedCol?.requests || []).filter((r) =>
    matches(query, r.name, r.method, r.url)
  );

  // At the workspace level any local/git workspace in the list can be shared as
  // a snapshot; once one is picked, only that one matters.
  const showCredentialsToggle = pickedWs
    ? isSnapshotWs(pickedWs)
    : (workspaces || []).some(isSnapshotWs);

  const placeholder =
    level === 'workspaces'
      ? 'Search workspaces…'
      : level === 'collections'
      ? `Search in ${pickedWs?.name || 'workspace'}…`
      : `Search in ${pickedCol?.name || 'collection'}…`;

  return (
    <div className="chat-ref-picker chat-share-picker">
      <div className="chat-share-picker-head">
        {level !== 'workspaces' ? (
          <button className="chat-icon-btn" title="Back" onClick={goBack} disabled={sending}>
            <ChevronLeft size={15} />
          </button>
        ) : (
          <Send size={13} style={{ color: '#8869eb', flexShrink: 0 }} />
        )}
        <span className="chat-share-picker-title">
          {level === 'workspaces' && 'Share a workspace, collection or request'}
          {level === 'collections' && (pickedWs?.name || 'Collections')}
          {level === 'requests' && (pickedCol?.name || 'Requests')}
        </span>
        <button className="chat-icon-btn" onClick={onClose} disabled={sending}>
          <X size={14} />
        </button>
      </div>

      <div className="chat-share-picker-search">
        <Search size={13} />
        <input
          autoFocus
          value={query}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              if (query) setQuery('');
              else if (level !== 'workspaces') goBack();
              else onClose();
            }
          }}
        />
      </div>

      {error && <div className="chat-share-picker-error">{error}</div>}

      {/* Only snapshot (local/git) workspaces carry the real request data — a
          cloud share sends a link and the recipient reads the server's copy
          under its own permissions, so the choice does not exist there. */}
      {!sending && showCredentialsToggle && (
        <div className="chat-share-picker-creds">
          <ShareCredentialsToggle
            includeCredentials={includeCredentials}
            onChange={setIncludeCredentials}
            labelId="picker-creds-label"
          />
        </div>
      )}

      <div className="chat-share-picker-list">
        {sending && (
          <div className="chat-share-picker-hint">
            <Loader size={13} className="animate-spin" /> Sharing…
          </div>
        )}

        {!sending && level === 'workspaces' && (
          <>
            {visibleWorkspaces.length === 0 && (
              <div className="chat-share-picker-hint">No workspaces found.</div>
            )}
            {visibleWorkspaces.map((w) => (
              <div key={w.id} className="chat-ref-item chat-share-row" onClick={() => drillIntoWorkspace(w)}>
                <Boxes size={14} />
                <span className="chat-share-row-label">{w.name}</span>
                {isSnapshotWs(w) && (
                  <span className="chat-share-row-badge" title="Shared as a frozen snapshot">
                    <HardDrive size={10} /> local
                  </span>
                )}
                <button
                  className="chat-share-row-action"
                  title="Share whole workspace"
                  onClick={(e) => {
                    e.stopPropagation();
                    shareWorkspace(w);
                  }}
                >
                  Share
                </button>
                <ChevronRight size={13} className="chat-share-row-caret" />
              </div>
            ))}
          </>
        )}

        {!sending && level === 'collections' && (
          <>
            {loading && (
              <div className="chat-share-picker-hint">
                <Loader size={13} className="animate-spin" /> Loading collections…
              </div>
            )}
            {!loading && visibleCollections.length === 0 && (
              <div className="chat-share-picker-hint">No collections found.</div>
            )}
            {!loading &&
              visibleCollections.map((c) => (
                <div key={c.id} className="chat-ref-item chat-share-row" onClick={() => drillIntoCollection(c)}>
                  <Folder size={14} />
                  <span className="chat-share-row-label">{c.name}</span>
                  <span className="chat-share-row-meta">{(c.requests || []).length}</span>
                  <button
                    className="chat-share-row-action"
                    title="Share collection"
                    onClick={(e) => {
                      e.stopPropagation();
                      doShare({ type: 'collection', item: c, workspace: pickedWs });
                    }}
                  >
                    Share
                  </button>
                  <ChevronRight size={13} className="chat-share-row-caret" />
                </div>
              ))}
          </>
        )}

        {!sending && level === 'requests' && (
          <>
            {visibleRequests.length === 0 && (
              <div className="chat-share-picker-hint">No requests found.</div>
            )}
            {visibleRequests.map((r) => (
              <div
                key={r.id}
                className="chat-ref-item chat-share-row"
                onClick={() => doShare({ type: 'request', item: r, workspace: pickedWs })}
              >
                <span className="chat-share-method" style={{ color: methodColor(r.method) }}>
                  {(r.method || 'GET').toUpperCase()}
                </span>
                <span className="chat-share-row-label">{r.name || r.url || 'Untitled'}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
